import {AnniversaryEvent, BirthdayEvent, CalendarPTOEvent} from 'client';
import {
  addDays,
  endOfMonth,
  format,
  isSameDay,
  isSameMonth,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import {t} from 'i18next';

import {buildPersonOverlapLayers} from './calendarFills';

/** A single person's PTO span, with parsed local-midnight dates. */
export interface PtoPerson {
  id: string;
  name: string;
  ldap: string;
  photoUrl?: string | null;
  /** First out day (inclusive, local midnight). */
  start: Date;
  /** Last out day (inclusive, local midnight). */
  end: Date;
  /** Why this person is in the viewer's cohort (relationship key, e.g. "peers"). */
  reason?: string;
}

/** A recurring birthday, placed on the grid by month/day regardless of year. */
export interface BirthdayPerson {
  id: string;
  name: string;
  ldap: string;
  photoUrl?: string | null;
  /** 1-indexed month (1-12). */
  month: number;
  /** Day of month (1-31). */
  day: number;
  /** Why this person is in the viewer's cohort (relationship key, e.g. "peers"). */
  reason?: string;
}

/** A recurring work anniversary, placed on the grid by month/day (hire date). */
export interface AnniversaryPerson {
  id: string;
  name: string;
  ldap: string;
  photoUrl?: string | null;
  /** 1-indexed month (1-12). */
  month: number;
  /** Day of month (1-31). */
  day: number;
  /** Years of service reached on the upcoming/current anniversary. */
  years: number;
  /** Why this person is in the viewer's cohort (relationship key, e.g. "peers"). */
  reason?: string;
}

const RELATION_KEYS = new Set([
  // Comment out to hide obvious relationships
  // 'manager',
  // 'direct_reports',
  // 'peers',
  // 'reporting_chain',
  'direct_plus_one',
  'team_people',
  'subscribed',
  'gratitude_sent',
  'gratitude_received',
]);

export const relationLabel = (reason?: string): string | null =>
  reason && RELATION_KEYS.has(reason) ? t(`home_reason_${reason}`) : null;

/** "Jun 3" for a single day, or "Jun 3–7" / "Jun 30–Jul 2" for a span. */
export const spanLabel = (person: PtoPerson): string => {
  if (isSameDay(person.start, person.end)) {
    return `${format(person.start, 'MMM d')}`;
  }
  const end = isSameMonth(person.start, person.end)
    ? format(person.end, 'd')
    : format(person.end, 'MMM d');
  return `${format(person.start, 'MMM d')}–${end}`;
};

/** "Jun 12" for a recurring birthday, rendered against the viewed year. */
export const birthdayLabel = (person: BirthdayPerson, year: number): string =>
  format(new Date(year, person.month - 1, person.day), 'MMM d');

/** "7 years" / "1 year" — the milestone being reached on the anniversary. */
export const anniversaryYearsLabel = (years: number): string =>
  `${years} ${years === 1 ? 'year' : 'years'}`;

/**
 * One person's pill within a day cell. `isStart`/`isEnd` round that person's own
 * span edges. Fill color comes from {@link CalendarGrid.overlapLayers} at render.
 */
export interface DaySegment {
  person: PtoPerson;
  isStart: boolean;
  isEnd: boolean;
}

/** Month grid plus per-person overlap depth (drives PTO fill in MiniTeamCalendar). */
export interface CalendarGrid {
  days: CalendarDay[];
  overlapLayers: Map<string, number>;
}

/** One cell of the month grid. */
export interface CalendarDay {
  date: Date;
  /** False for the leading/trailing days that belong to a neighboring month. */
  inMonth: boolean;
  isToday: boolean;
  /** Before today (de-emphasized in the grid). */
  isPast: boolean;
  /** Everyone out on this day. */
  people: PtoPerson[];
  /**
   * People whose PTO first becomes visible on this day — i.e. the first grid cell
   * their span covers. Anchors a facepile so you can see *who* is out at the
   * start of each run. Spans that began before the grid anchor on the grid's
   * first day.
   */
  startingPeople: PtoPerson[];
  isOut: boolean;
  /** Per-person colored pills for this day, each with its own span edges. */
  segments: DaySegment[];
  /** Anyone whose (recurring) birthday lands on this day. */
  birthdays: BirthdayPerson[];
  /** Anyone whose (recurring) work anniversary lands on this day. */
  anniversaries: AnniversaryPerson[];
}

/** Weeks rendered per month — always 6 so the grid height never jumps. */
const GRID_DAYS = 42;

export const parseTeamPtoApiDate = (value: string): Date => {
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day);
};

const dayCovers = (day: Date, person: PtoPerson): boolean => {
  const dayMs = startOfDay(day).getTime();
  const startMs = startOfDay(person.start).getTime();
  const endMs = startOfDay(person.end).getTime();
  return dayMs >= startMs && dayMs <= endMs;
};

/** Map the API's PTO events onto the local-date span shape the calendar uses. */
export const toPtoPeople = (events: CalendarPTOEvent[]): PtoPerson[] =>
  events.map((event) => ({
    id: event.user_id,
    name: event.name,
    ldap: event.ldap,
    photoUrl: event.profile_photo_url,
    start: parseTeamPtoApiDate(event.start_date),
    end: parseTeamPtoApiDate(event.end_date),
    reason: event.reason,
  }));

/** Map the API's birthday events onto the recurring month/day shape. */
export const toBirthdayPeople = (events: BirthdayEvent[]): BirthdayPerson[] =>
  events.map((event) => ({
    id: event.employee_id,
    name: event.name,
    ldap: event.ldap,
    photoUrl: event.profile_photo_url,
    month: event.month,
    day: event.day,
    reason: event.reason,
  }));

/** Map the API's anniversary events onto the recurring month/day shape. */
export const toAnniversaryPeople = (events: AnniversaryEvent[]): AnniversaryPerson[] =>
  events.map((event) => ({
    id: event.employee_id,
    name: event.name,
    ldap: event.ldap,
    photoUrl: event.profile_photo_url,
    month: event.month,
    day: event.day,
    years: event.years,
    reason: event.reason,
  }));

const recurringCovers = (date: Date, person: {month: number; day: number}): boolean =>
  date.getMonth() + 1 === person.month && date.getDate() === person.day;

const birthdayCovers = (date: Date, person: BirthdayPerson): boolean =>
  recurringCovers(date, person);

/** First/last day the visible month grid spans (padded to whole weeks). */
export const monthGridBounds = (
  viewMonth: Date,
  weekStartsOn: 0 | 1 = 0
): {gridStart: Date; gridEnd: Date} => {
  const gridStart = startOfWeek(startOfMonth(viewMonth), {weekStartsOn});
  return {gridStart, gridEnd: addDays(gridStart, GRID_DAYS - 1)};
};

/**
 * What the tile shows: one calendar month, or `ahead` — last week plus the five
 * weeks after it, so today sits in the second row. Past PTO is never returned, so
 * late in a month the month grid is mostly empty past rows; `ahead` keeps one
 * week back (for recent birthdays and anniversaries) and shows the coming weeks.
 */
export type CalendarView = {kind: 'month'; month: Date} | {kind: 'ahead'};

/** The dates a {@link CalendarView} resolves to. */
export interface CalendarWindow {
  gridStart: Date;
  gridEnd: Date;
  /** Days the list view covers: the month itself, or the whole grid for `ahead`. */
  rangeStart: Date;
  rangeEnd: Date;
  /** Month that owns the grid (days outside it render subdued); null for `ahead`. */
  month: Date | null;
}

const monthWindow = (month: Date, weekStartsOn: 0 | 1): CalendarWindow => ({
  ...monthGridBounds(month, weekStartsOn),
  rangeStart: startOfMonth(month),
  rangeEnd: endOfMonth(month),
  month: startOfMonth(month),
});

/**
 * Resolve a view to its grid. When today is in the month grid's second row the
 * `ahead` grid is exactly that month grid, so it falls back to the current month
 * (same label and subdued trailing days as a month view).
 */
export const calendarWindow = (
  view: CalendarView,
  weekStartsOn: 0 | 1 = 0,
  today: Date = new Date()
): CalendarWindow => {
  if (view.kind === 'month') {
    return monthWindow(view.month, weekStartsOn);
  }
  const gridStart = addDays(startOfWeek(startOfDay(today), {weekStartsOn}), -7);
  const current = monthWindow(today, weekStartsOn);
  if (isSameDay(gridStart, current.gridStart)) {
    return current;
  }
  const gridEnd = addDays(gridStart, GRID_DAYS - 1);
  return {gridStart, gridEnd, rangeStart: gridStart, rangeEnd: gridEnd, month: null};
};

const longMonthFormatter = new Intl.DateTimeFormat('en-US', {month: 'long', year: 'numeric'});
const shortMonthFormatter = new Intl.DateTimeFormat('en-US', {month: 'short'});
const shortMonthYearFormatter = new Intl.DateTimeFormat('en-US', {month: 'short', year: 'numeric'});

/**
 * "October 2026" for a month; "Oct – Nov 2026" (or "Dec 2026 – Jan 2027") for
 * `ahead`: from today's month (not the week-back row's) to the month the bottom
 * row starts in.
 */
export const calendarWindowLabel = (
  {gridEnd, month}: CalendarWindow,
  today: Date = new Date()
): string => {
  if (month) {
    return longMonthFormatter.format(month);
  }
  const first = today;
  const last = addDays(gridEnd, -6);
  if (isSameMonth(first, last)) {
    return longMonthFormatter.format(first);
  }
  if (first.getFullYear() === last.getFullYear()) {
    return `${shortMonthFormatter.format(first)} – ${shortMonthYearFormatter.format(last)}`;
  }
  return `${shortMonthYearFormatter.format(first)} – ${shortMonthYearFormatter.format(last)}`;
};

/**
 * Build the 6×7 grid. PTO pill rendering splits three concerns:
 * - **Shape** (`isStart`/`isEnd`): row neighbors only — pills break at week boundaries.
 * - **Color** (`overlapLayers`): gray→blue ramp by overlap depth, sticky per person.
 * - **Content** (`startingPeople`, facepiles): grid-day neighbors for who anchors avatars.
 */
export const buildCalendarDays = (
  {gridStart, month}: Pick<CalendarWindow, 'gridStart' | 'month'>,
  people: PtoPerson[],
  birthdays: BirthdayPerson[] = [],
  anniversaries: AnniversaryPerson[] = []
): CalendarGrid => {
  const today = new Date();
  const todayStart = startOfDay(today).getTime();

  const base = Array.from({length: GRID_DAYS}, (_, index) => {
    const date = addDays(gridStart, index);
    const onLeave = people.filter((person) => dayCovers(date, person));
    return {
      date,
      inMonth: month ? isSameMonth(date, month) : true,
      isToday: isSameDay(date, today),
      isPast: date.getTime() < todayStart,
      people: onLeave,
      isOut: onLeave.length > 0,
      birthdays: birthdays.filter((person) => birthdayCovers(date, person)),
      anniversaries: anniversaries.filter((person) => recurringCovers(date, person)),
    };
  });

  const overlapLayers = buildPersonOverlapLayers(base);

  const days = base.map((day, index) => {
    const column = index % 7;
    const prevDay = column > 0 ? base[index - 1] : undefined;
    const nextDay = column < 6 ? base[index + 1] : undefined;
    // Facepile anchors use consecutive grid days (can cross week rows).
    const prevPeople = index > 0 ? base[index - 1].people : [];
    const startingPeople = day.people.filter(
      (person) => !prevPeople.some((prev) => prev.id === person.id)
    );
    const segments = day.people.map((person) => ({
      person,
      isStart: !prevDay?.people.some((p) => p.id === person.id),
      isEnd: !nextDay?.people.some((p) => p.id === person.id),
    }));
    return {
      ...day,
      startingPeople,
      segments,
    };
  });

  return {days, overlapLayers};
};

/** Everyone out at any point between `start` and `end` (inclusive local days). */
export const peopleOutInRange = (start: Date, end: Date, people: PtoPerson[]): PtoPerson[] =>
  people.filter(
    (person) =>
      startOfDay(person.start).getTime() <= end.getTime() &&
      startOfDay(person.end).getTime() >= start.getTime()
  );

/**
 * The date a recurring month/day lands on between `start` and `end`, or null.
 * A range can cross New Year (six weeks ahead in December), so check both years.
 */
export const recurringDateInRange = (
  person: {month: number; day: number},
  start: Date,
  end: Date
): Date | null => {
  for (const year of [start.getFullYear(), end.getFullYear()]) {
    const date = new Date(year, person.month - 1, person.day);
    if (recurringCovers(date, person) && date >= start && date <= end) {
      return date;
    }
  }
  return null;
};

/** Recurring events (birthdays, anniversaries) landing in the range, sorted by date. */
export const recurringInRange = <T extends {month: number; day: number; name: string}>(
  start: Date,
  end: Date,
  people: T[]
): T[] =>
  people
    .map((person) => ({person, date: recurringDateInRange(person, start, end)}))
    .filter((entry): entry is {person: T; date: Date} => entry.date !== null)
    .sort(
      (left, right) =>
        left.date.getTime() - right.date.getTime() ||
        left.person.name.localeCompare(right.person.name)
    )
    .map((entry) => entry.person);

/** True when someone's recurring birthday lands on today (drives the festive accent). */
export const hasBirthdayToday = (birthdays: BirthdayPerson[]): boolean => {
  const today = new Date();
  return birthdays.some((person) => birthdayCovers(today, person));
};

/** True when someone's work anniversary lands on today (drives the festive accent). */
export const hasAnniversaryToday = (anniversaries: AnniversaryPerson[]): boolean => {
  const today = new Date();
  return anniversaries.some((person) => recurringCovers(today, person));
};

/**
 * Month of the soonest span that hasn't ended yet (on or after `from`), or null
 * when nobody is out. Used to skip ahead on first load when the next six weeks
 * are empty — e.g. open straight to August if that's when the next person is out.
 */
export const firstUpcomingOutMonth = (people: PtoPerson[], from: Date): Date | null => {
  const fromStart = startOfDay(from).getTime();
  const soonest = people
    .filter((person) => person.end.getTime() >= fromStart)
    .reduce<PtoPerson | null>(
      (earliest, person) =>
        !earliest || person.start.getTime() < earliest.start.getTime() ? person : earliest,
      null
    );
  return soonest ? startOfMonth(soonest.start) : null;
};
